// ----------------------------------------------------------------------------
// 🎵 MEMBER UPLOAD (Song Studio)
// A standalone page that lets ANY active church member (not just the admin)
// sign in and upload a rehearsal recording for chord analysis. The backend
// already authorizes any active member (EnsureWriteAccessAsync), so this page
// only needs to gate on church membership:
//   - has a church  -> upload + view the church's analyzed songs
//   - pending       -> waiting for an admin to approve
//   - no church     -> join with an invite code, or create a church
// Reuses the upload orchestration (useChordLibrary) and the Song Studio parts
// the admin page uses.
// ----------------------------------------------------------------------------

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Church, Clock, Lock, LogOut, Moon, Sun } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { isEmptyChurchId } from '@app/utils/churchId';
import { checkChurchLimits } from '@app/api/subscription';
import { churchesApi, groupsApi, membershipsApi } from '@app/api/organization';
import { CHORD_LIBRARY_SONG_LIMIT } from '@app/api/chords';
import { ChurchMembershipState, type Membership } from '@app/types';
import { logger } from '@app/utils/logger';
import { useTheme } from '@app/theme/theme';
import { useChordLibrary } from '@admin/worshipplanner/hooks/useChordLibrary';
import { UploadSongModal } from '@admin/worshipplanner/components/chords/UploadSongModal';
import {
  LibraryMeter,
  StudioDropZone,
  StudioRows,
  UploadBanner,
  countSaved,
  type UploadStatus,
} from '@admin/worshipplanner/components/chords/SongStudioParts';
import { Avatar, Button, ConfirmDialog, ErrorState, Input, Spinner, StatusPill, ToastProvider, initialsOf, useToast } from '@app/components/ui';
import { apiErrorMessage } from '@app/lib/apiErrors';

// Where "read the guide" points: the public admin guide on the marketing site
// (churchnow.co). Kept as a single constant so it is trivial to repoint later.
const ADMIN_GUIDE_URL = 'https://churchnow.co/guide/admin';

type UploadArgs = [File, string, string | undefined, string | undefined, string | undefined];

const cardClass = 'rounded-[24px] border border-line bg-card px-[26px] py-8 shadow-wp';

export const MemberUpload: React.FC = () => (
  // /upload keeps its toasts up a little longer than the admin app (2.8s).
  <ToastProvider duration={2800}>
    <MemberUploadInner />
  </ToastProvider>
);

const MemberUploadInner: React.FC = () => {
  const { user, logout } = useAuth();
  const toast = useToast();
  const tokenChurchId = user?.churchId;
  // Song Studio defaults to dark; the member's own choice persists, and the
  // admin theme is restored when they leave.
  const { isDark, toggle } = useTheme('member');

  // ── Resolving which church this member belongs to ────────────────────────
  //
  // `user.churchId` comes from the login response / JWT claim, which the
  // Authentication service derives from its own DENORMALIZED copy of
  // memberships. When that copy is missing or stale — it is written by the
  // Organizations service calling back on each membership change — a genuine,
  // active member signs in with an empty churchId.
  //
  // Trusting it alone meant telling such a member to "Join a church to upload
  // songs", with a Create-a-church button as their only option, while the
  // Organizations service was perfectly willing to accept their uploads. There
  // was no way out of that from the UI.
  //
  // So when the claim is empty we ask the service that actually OWNS
  // memberships before concluding anything.
  const [resolvedChurchId, setResolvedChurchId] = useState<string | null>(null);
  const [churchLookupDone, setChurchLookupDone] = useState(false);

  // A PENDING membership is not the same as having no church. The member asked
  // to join and nobody has approved them yet, so an empty churchId is the
  // correct answer — but "Join a church to upload songs" with a Create-a-church
  // button is the wrong thing to say about it, and pushes them into making a
  // duplicate church. Tracked separately so the page can say what is actually
  // happening.
  const [pendingChurchId, setPendingChurchId] = useState<string | null>(null);
  const [pendingChurchName, setPendingChurchName] = useState<string | null>(null);

  useEffect(() => {
    if (!isEmptyChurchId(tokenChurchId)) {
      // The claim is good; no lookup needed.
      setChurchLookupDone(true);
      return;
    }
    if (!user?.id) {
      setChurchLookupDone(true);
      return;
    }

    let cancelled = false;
    membershipsApi
      .getUserMemberships(user.id)
      .then((memberships) => {
        if (cancelled) return;
        // Only an ACTIVE membership grants access. The endpoint filters on the
        // row's soft-delete flag, not on state, so pending requests come back
        // too and must be ignored here. A null/undefined state counts as active,
        // matching the backend's own rule in
        // OrganizationDatabase.GetUserChurchMembershipStateAsync.
        const active = memberships.find(
          (m) =>
            m.isActive !== false &&
            (m.churchState === undefined || m.churchState === null || m.churchState === ChurchMembershipState.Active) &&
            !isEmptyChurchId(m.churchId),
        );
        if (active) {
          logger.warn(
            'Signed-in user had no churchId on their token but IS an active member of a church; ' +
              'recovering from the organizations service. The Authentication service copy is stale.',
            { churchId: active.churchId },
          );
          setResolvedChurchId(active.churchId);
          return;
        }

        // No active membership, but an unapproved join request is a different
        // situation and worth naming.
        const pending = memberships.find(
          (m) => m.isActive !== false && m.churchState === ChurchMembershipState.Pending && !isEmptyChurchId(m.churchId),
        );
        if (pending) {
          setPendingChurchId(pending.churchId);
          // Best effort: naming the church makes the message concrete, but the
          // membership row does not carry the name and the page reads fine
          // without it, so a failure here is deliberately not surfaced.
          churchesApi
            .getById(pending.churchId)
            .then((church) => {
              if (!cancelled) setPendingChurchName(church?.name ?? null);
            })
            .catch(() => undefined);
        }
      })
      .catch((err) => {
        // Nothing to recover with — fall through to the join/create prompt.
        logger.error('Could not resolve church membership for the Song Studio:', err);
      })
      .finally(() => {
        if (!cancelled) setChurchLookupDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [tokenChurchId, user?.id]);

  const churchId = !isEmptyChurchId(tokenChurchId) ? tokenChurchId : (resolvedChurchId ?? undefined);
  const hasChurch = !isEmptyChurchId(churchId);

  // Header sub-line: the church's name once we know they belong to one.
  const [churchName, setChurchName] = useState<string | null>(null);
  useEffect(() => {
    if (!hasChurch || !churchId) return;
    let cancelled = false;
    churchesApi
      .getById(churchId)
      .then((c) => {
        if (!cancelled) setChurchName(c?.name ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [hasChurch, churchId]);

  // Only load/watch the church library when the member actually has a church.
  const { uploads, loading, loadError, uploadSong, removeUpload, renameUpload, reload } = useChordLibrary(
    hasChurch && churchId ? churchId : undefined,
  );
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);

  // Subscription paywall: does this church's tier include Chord AI?
  // null = still checking (render the normal UI; the backend enforces anyway),
  // false = show the upgrade prompt instead of upload affordances.
  const [chordAiAllowed, setChordAiAllowed] = useState<boolean | null>(null);
  const isAdmin = user?.userRole === 'Admin' || (user?.adminChurches?.length ?? 0) > 0;
  useEffect(() => {
    if (!hasChurch || !churchId) return;
    let cancelled = false;
    checkChurchLimits(churchId)
      .then((limits) => {
        if (!cancelled) setChordAiAllowed(limits.canUseChordAi);
      })
      .catch(() => {
        // Fail open: the chord + organizations services still enforce the tier.
        if (!cancelled) setChordAiAllowed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [hasChurch, churchId]);

  const openUpload = (file?: File | null) => {
    setDroppedFile(file ?? null);
    setUploadOpen(true);
  };
  const closeUpload = () => {
    setUploadOpen(false);
    setDroppedFile(null);
  };

  // Upload lifecycle for the on-page indicator: 'uploading' means the file is
  // still being sent from this device (stay on the page); 'done' means it has
  // reached our servers and is analyzing (safe to sign out or leave).
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadTitle, setUploadTitle] = useState('');
  const lastArgs = useRef<UploadArgs | null>(null);
  const handleUpload = async (...args: UploadArgs) => {
    lastArgs.current = args;
    setUploadTitle(args[1]);
    setUploadStatus('uploading');
    const result = await uploadSong(...args);
    setUploadStatus(result === 'ok' ? 'done' : result === 'failed' ? 'error' : 'idle');
  };
  // Auto-dismiss the success/error note after a short while.
  useEffect(() => {
    if (uploadStatus !== 'done' && uploadStatus !== 'error') return;
    const t = setTimeout(() => setUploadStatus('idle'), 12000);
    return () => clearTimeout(t);
  }, [uploadStatus]);

  // Signing out mid-upload cancels it, so ask first.
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const signOut = () => (uploadStatus === 'uploading' ? setConfirmSignOut(true) : void logout());

  const saved = countSaved(uploads);
  const atLimit = saved >= CHORD_LIBRARY_SONG_LIMIT;
  const subLine = hasChurch ? churchName || user?.username || '' : pendingChurchId ? 'Request pending' : user?.username || '';

  return (
    <div className="relative min-h-screen bg-bg text-t1">
      <header
        className="sticky top-0 z-30 bg-hdr border-b border-line"
        style={{ backdropFilter: 'saturate(1.4) blur(16px)', WebkitBackdropFilter: 'saturate(1.4) blur(16px)' }}
      >
        <div className="max-w-[780px] mx-auto h-16 px-4 flex items-center gap-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <img src="/favicon.svg" alt="" className="w-[30px] h-[30px] rounded-[8px] block shrink-0" />
            <div className="min-w-0">
              <p className="text-[15px] font-semibold leading-tight">Song Studio</p>
              <p className="text-[12px] text-t4 truncate">{subLine}</p>
            </div>
          </div>
          {isAdmin && hasChurch && (
            <Link
              to="/admin/dashboard"
              className="h-[34px] px-3 rounded-full border border-line2 bg-fill !text-t2 hover:!text-t1 text-[13px] font-medium flex items-center whitespace-nowrap"
            >
              Admin app
            </Link>
          )}
          <button
            type="button"
            onClick={toggle}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="w-9 h-9 rounded-full bg-fill2 text-t3 hover:text-t1 flex items-center justify-center shrink-0"
          >
            {isDark ? <Sun size={16} strokeWidth={1.8} aria-hidden="true" /> : <Moon size={16} strokeWidth={1.8} aria-hidden="true" />}
          </button>
          <span className="hidden min-[400px]:flex" title={user?.username}>
            <Avatar name={user?.username} size={34} />
          </span>
          <button
            type="button"
            onClick={signOut}
            className="h-9 px-3.5 rounded-full border border-line2 bg-fill text-t2 hover:bg-fill2 hover:text-t1 text-[13px] font-medium flex items-center gap-[7px] shrink-0"
          >
            <LogOut size={15} strokeWidth={2} aria-hidden="true" />
            <span className="hidden min-[480px]:inline">Sign out</span>
          </button>
        </div>
      </header>

      <main className="max-w-[780px] mx-auto px-4 pt-8 pb-[120px]">
        {!hasChurch && !churchLookupDone ? (
          // Still asking the organizations service whether they belong to a
          // church. Showing the join/create prompt here would flash the wrong
          // answer at a member who does have one.
          <div aria-busy="true" className="wp-fade flex flex-col gap-3">
            <div className="text-[13.5px] text-t4 flex items-center gap-2.5 mb-1.5">
              <Spinner size={14} />
              Finding your church…
            </div>
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3.5 p-4 rounded-[18px] border border-line bg-card">
                <span className="wp-skeleton w-[42px] h-[42px] rounded-[12px]" />
                <div className="flex-1 flex flex-col gap-2">
                  <span className="wp-skeleton h-3 w-3/5 rounded-[6px]" />
                  <span className="wp-skeleton h-2.5 w-[35%] rounded-[6px]" />
                </div>
              </div>
            ))}
          </div>
        ) : !hasChurch && pendingChurchId ? (
          // ── Join request awaiting approval ───────────────────────────────
          //
          // Deliberately NOT the create-a-church prompt. This member has
          // already asked to join; telling them to create a church would get
          // them a duplicate church and still no access to the one they want.
          <div data-testid="pending-approval" className="max-w-[480px] mx-auto mt-6 wp-rise">
            <div className={`${cardClass} text-center`}>
              <span className="w-[60px] h-[60px] rounded-full bg-info-bg text-info flex items-center justify-center mx-auto mb-[18px]" aria-hidden="true">
                <Clock size={26} strokeWidth={1.8} />
              </span>
              <h1 className="text-[24px] font-semibold tracking-[-0.02em] leading-[1.25]">Waiting for approval</h1>
              <p className="text-[14.5px] text-t3 mt-2 mx-auto leading-[1.6] max-w-[360px] wp-pretty">
                {pendingChurchName
                  ? "Your request is with their admin. Once they approve you, your church's songs appear here."
                  : "Your request to join is with the church admin. Once they approve you, your church's songs appear here."}
              </p>
              <div className="flex items-center gap-3 px-3.5 py-3 rounded-[14px] bg-fill mt-[22px] mb-[18px] text-left">
                <span className="w-[38px] h-[38px] rounded-[11px] bg-acc-bg2 text-acc font-bold text-[13px] flex items-center justify-center shrink-0" aria-hidden="true">
                  {initialsOf(pendingChurchName || 'Church')}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold truncate">{pendingChurchName || 'Your church'}</div>
                  <div className="text-[12.5px] text-t4">Join request sent</div>
                </div>
                <StatusPill status="Pending" />
              </div>
              <Button size="lg" shape="form" block className="!h-12" onClick={() => window.location.reload()}>
                Check again
              </Button>
              <p className="text-[12.5px] text-t4 mt-3.5 leading-normal">
                Only a church admin can approve you. Nudge your worship leader if it has been a while.
              </p>
            </div>
          </div>
        ) : !hasChurch ? (
          <NoChurch
            userId={user?.id}
            onJoined={(m) => {
              if (m.churchState === ChurchMembershipState.Active) setResolvedChurchId(m.churchId);
              else {
                setPendingChurchId(m.churchId);
                churchesApi
                  .getById(m.churchId)
                  .then((c) => setPendingChurchName(c?.name ?? null))
                  .catch(() => undefined);
              }
            }}
          />
        ) : loadError ? (
          <ErrorState title="Couldn't load your church's songs" body={loadError} onRetry={() => reload()} />
        ) : (
          // ── Has church: upload + the church's analyzed songs ──────────────
          <div className="wp-rise">
            <div className="flex flex-wrap items-end justify-between gap-[18px] mb-[22px]">
              <div className="flex-[1_1_320px] min-w-0">
                <h1 className="m-0 mb-1.5 font-semibold tracking-[-0.03em] leading-[1.2] pb-[.08em]" style={{ fontSize: 'clamp(28px, 5vw, 36px)' }}>
                  Your church&apos;s songs
                </h1>
                <p className="text-[14.5px] text-t3 leading-[1.6] wp-pretty">
                  Upload a rehearsal recording and we&apos;ll detect the chords and key for {churchName ? `${churchName}'s` : 'your'} worship team.
                </p>
              </div>
              <LibraryMeter count={saved} className="flex-[0_1_230px] min-w-[200px]" />
            </div>

            <UploadBanner
              status={uploadStatus}
              title={uploadTitle}
              onDismiss={() => setUploadStatus('idle')}
              onRetry={lastArgs.current ? () => handleUpload(...lastArgs.current!) : undefined}
            />

            {loading ? (
              <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading songs">
                {[0, 1, 2].map((i) => (
                  <span key={i} className="wp-skeleton h-[74px] rounded-[18px]" />
                ))}
              </div>
            ) : (
              <>
                {chordAiAllowed === false ? (
                  <UpgradeCard isAdmin={isAdmin} />
                ) : (
                  <StudioDropZone empty={uploads.length === 0} atLimit={atLimit} onFile={openUpload} onReject={(m) => toast(m, 'error')} />
                )}
                <StudioRows uploads={uploads} onRemove={removeUpload} onRename={renameUpload} onRemoved={(t) => toast(`Removed "${t}"`)} />
              </>
            )}
          </div>
        )}
      </main>

      {hasChurch && (
        <UploadSongModal
          isOpen={isUploadOpen}
          onClose={closeUpload}
          onUpload={(...a) => handleUpload(...(a as UploadArgs))}
          initialFile={droppedFile}
          existingUploads={uploads}
        />
      )}

      <ConfirmDialog
        isOpen={confirmSignOut}
        onClose={() => setConfirmSignOut(false)}
        onConfirm={() => void logout()}
        title="Your upload is still sending"
        message={`Signing out now cancels "${uploadTitle}". Wait for the "Uploaded" message, then sign out.`}
        cancelText="Stay"
        confirmText="Sign out anyway"
      />
    </div>
  );
};

// Shown instead of the upload UI when the church's tier lacks Chord AI.
const UpgradeCard: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => (
  <section aria-label="Upgrade required" className="rounded-[24px] border border-line bg-card px-6 py-9 text-center mb-2">
    <div className="w-14 h-14 rounded-[16px] bg-amb-bg text-amb flex items-center justify-center mx-auto mb-4" aria-hidden="true">
      <Lock size={24} strokeWidth={2} />
    </div>
    <h2 className="text-[19px] font-semibold">Song Studio isn&apos;t in your church&apos;s plan</h2>
    <p className="text-[14px] text-t3 mt-2 mb-5 mx-auto max-w-[440px] leading-[1.55]">
      Song uploads with chord detection require a Basic, Pro, or Enterprise subscription.
      {isAdmin ? ' Upgrade to unlock uploads for your whole church.' : ' Ask your church admin to upgrade.'} Existing songs stay
      listed with their deletion dates.
    </p>
    {isAdmin ? (
      <Link
        to="/admin/subscription"
        className="inline-flex items-center h-[42px] px-5 rounded-full bg-acc hover:bg-acc-h !text-white text-[14px] font-semibold"
      >
        Upgrade your plan
      </Link>
    ) : (
      <a
        href={ADMIN_GUIDE_URL}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center h-[42px] px-5 rounded-full border border-line2 bg-fill !text-t1 text-[14px] font-medium"
      >
        See what&apos;s included
      </a>
    )}
  </section>
);

/** No church yet: join with an invite code, or create one. */
const NoChurch: React.FC<{ userId?: string; onJoined: (m: Membership) => void }> = ({ userId, onJoined }) => {
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim();
    if (c.replace(/[^A-Z0-9]/gi, '').length < 6) {
      setErr('Enter the full invite code.');
      return;
    }
    if (!userId) return;
    setBusy(true);
    setErr('');
    try {
      // Church-level code first, then a team code — the same order as /invite/:code.
      let m: Membership;
      try {
        m = await churchesApi.joinByInviteCode(c, userId);
      } catch {
        m = await groupsApi.joinByCode(c, userId);
      }
      onJoined(m);
    } catch (error: any) {
      setErr(apiErrorMessage(error, 'That code didn’t match a church. Check it with your admin.', 'member'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[480px] mx-auto mt-6 wp-rise">
      <div className={cardClass}>
        <div className="w-14 h-14 rounded-[16px] bg-acc-bg2 text-acc flex items-center justify-center mb-[18px]" aria-hidden="true">
          <Church size={26} strokeWidth={1.8} />
        </div>
        <h1 className="text-[24px] font-semibold tracking-[-0.02em] leading-[1.25]">Join a church to upload songs</h1>
        <p className="text-[14.5px] text-t3 mt-2 leading-[1.6] wp-pretty">
          Song Studio uploads are shared with your church&apos;s worship team. Enter the invite code your church admin gave you.
        </p>
        <form onSubmit={submit} noValidate className="mt-[22px]">
          <label htmlFor="join-code" className="wp-label">
            Invite code
          </label>
          <div className="flex gap-2">
            <Input
              id="join-code"
              height={50}
              mono
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setErr('');
              }}
              placeholder="GRACE-7KQ2"
              autoComplete="off"
              invalid={!!err}
              className="flex-1 min-w-0 !text-[17px] font-bold tracking-[.08em] uppercase !px-4"
            />
            <Button type="submit" shape="form" loading={busy} className="!h-[50px] !rounded-[12px] shrink-0">
              Join
            </Button>
          </div>
          <div className="text-[12.5px] text-acc mt-1.5 min-h-4" role="alert">
            {err}
          </div>
        </form>
        <div className="flex items-center gap-3 text-[12px] text-t5 mt-2.5 mb-4" aria-hidden="true">
          <span className="flex-1 h-px bg-line2" />
          or
          <span className="flex-1 h-px bg-line2" />
        </div>
        <div className="flex flex-col gap-2.5">
          <Link
            to="/create-church"
            className="h-[46px] rounded-full border border-line2 bg-fill !text-t1 text-[14px] font-medium flex items-center justify-center gap-2 hover:bg-fill2"
          >
            + Create a church
          </Link>
          <a href={ADMIN_GUIDE_URL} target="_blank" rel="noreferrer" className="text-[13.5px] text-center p-1.5">
            Read the admin guide
          </a>
        </div>
      </div>
    </div>
  );
};

export default MemberUpload;
